/** SUNTRONIX ONE — Enterprise OS sidebar model.
 *  Compact primary groups → expandable sub-navigation. Every entry maps to an
 *  EXISTING route; nothing here changes permissions (filtered via canSee). */
import {
  Home, FileText, PackageCheck, Laptop, Users,
  Wrench, Receipt, BarChart3, Settings, MessageCircle,
} from "lucide-react";
import { SPACES_SUNTRONIX, SUBSPACES } from "@/constants/spaces";

export const OS_NAV = [
  { id: "home", label: "Home", icon: Home, to: "/", extra: ["/home"],
    children: [
      { to: "/home", label: "My Work Today" },
      { to: "/today", label: "My Tasks" },
      { to: "/approvals/one", label: "Approvals" },
      { to: "/notifications", label: "Notifications" },
    ],
    advanced: [
      { to: "/tasks", label: "Task Manager (SLA)" }, { to: "/dashboard", label: "My Dashboard" }, { to: "/work", label: "My Work" }, { to: "/work/projects", label: "Projects & Tasks" },
      { to: "/sop", label: "SOP Centre" }, { to: "/vault", label: "Document Vault" }, { to: "/files", label: "ONE Files" }, { to: "/network", label: "ONE Network" }, { to: "/ledger", label: "ONE Ledger" }, { to: "/my-devices", label: "My Devices" },
      { to: "/feedback", label: "Product Feedback" }, { to: "/roadmap", label: "Product Roadmap" }, { to: "/whats-new", label: "What's New" },
    ] },
  {
    id: "sales", label: "Sales", icon: FileText, to: "/sales-desk",
    children: [
      { to: "/sales-desk", label: "Sales Desk" },
      { to: "/sales-desk/catalogue-enquiries", label: "Enquiries & RFQs" },
      { to: "/commercial/crm/quotations", label: "Quotations" },
      { to: "/commercial/customer-po", label: "Customer POs" },
      { to: "/commercial/followup", label: "Follow-ups" },
      { to: "/sales/walkin-feedback", label: "Walk-in / Service Feedback" },
    ],
    advanced: [
      { to: "/customer-action-required", label: "Customer Action Required" }, { to: "/commercial/contracts", label: "Contracts" }, { to: "/commercial/amc-pms", label: "AMC / PMS" }, { to: "/commercial/one-engage", label: "ONE Engage" },
    ],
  },
  {
    id: "orders", label: "Orders & Dispatch", icon: PackageCheck, to: "/commercial/one-orders", extra: ["/orders"],
    children: [
      { to: "/commercial/one-orders", label: "Orders" },
      { to: "/finance/order-closure", label: "Order Closure" },
      { to: "/orders/smart", label: "Smart Fulfilment (oversight)", adminOnly: true },
      { to: "/orders/command-centre", label: "Order Command Center" },
      { to: "/logistics/trace", label: "Multi-location & Trace" },
      { to: "/logistics/fulfilment", label: "Fulfilment & Dispatch" },
      { to: "/dispatch/delivery-challans", label: "Delivery Challans (from Quote)" },
      { to: "/reports/dc-added-items", label: "Items Added on DCs" },
      { to: "/shipments", label: "Shipments" },
      { to: "/pickups", label: "Reverse Pickups" },
    ],
    advanced: [
      { to: "/commercial/crm/vendor-pos", label: "Vendor POs (procurement)" }, { to: "/operations/employee-fulfilment", label: "Employee Fulfilment" }, { to: "/deployment-centre", label: "Deployment Control Centre" }, { to: "/deploy", label: "Deploy Device" }, { to: "/bulk-deployment", label: "Bulk Deployment" }, { to: "/operations/office-batches", label: "Office Batches" },
      { to: "/offboarding", label: "Recover / Offboard" }, { to: "/operations/asset-recovery", label: "Asset Recovery (chain of custody)" }, { to: "/admin/couriers", label: "Courier Partners" }, { to: "/control-tower", label: "Control Tower" },
    ],
  },
  {
    id: "service", label: "Service", icon: Wrench, to: "/service",
    children: [
      { to: "/service", label: "Service Tickets" },
      { to: "/service-desk", label: "Service Desk" },
      { to: "/service-centre", label: "Repairs (Service Centre)" },
      { to: "/requests", label: "Requests" },
      { to: "/sales/walkin-feedback", label: "Walk-in / Service Feedback" },
    ],
    advanced: [
      { to: "/service/rma", label: "RMA & Warranty" }, { to: "/operations/apple-services", label: "Apple Services" }, { to: "/operations/applecare-plus", label: "AppleCare+" }, { to: "/operations/field-service", label: "Field Service" }, { to: "/telephony", label: "Telephony" }, { to: "/commercial/csat", label: "CSAT & Feedback" },
    ],
  },
  {
    id: "inventory", label: "Assets & Inventory", icon: Laptop, to: "/assets",
    children: [
      { to: "/assets", label: "Assets" },
      { to: "/devices", label: "Device 360°" },
      { to: "/operations/stock-inventory", label: "Stock & Inventory" },
      { to: "/technology/product-master", label: "Product Master" },
      { to: "/catalogue-workbench", label: "Catalogue Workbench" },
    ],
    advanced: [
      { to: "/operations/daily-stock", label: "Daily Stock" }, { to: "/operations/multibrand-stock", label: "Multi-Brand Stock" }, { to: "/operations/customer-owned-stock", label: "Customer-Owned Stock" }, { to: "/warehouse", label: "Warehouse" }, { to: "/warehouse/returns", label: "Returns & Redeployment" }, { to: "/warehouse/device-status", label: "Warehouse Device Status" },
      { to: "/technology/product-intelligence", label: "Product Intelligence" }, { to: "/admin/product-intelligence/data-sources/apple-cto", label: "Apple CTO Master (Data Source)" }, { to: "/lifecycle/people", label: "Packages · Buffer · On/Offboarding" }, { to: "/lifecycle/replenishment", label: "Automatic Replenishment" }, { to: "/deployments/inbox", label: "Deployment Inbox" },
      { to: "/warranty", label: "Warranty" }, { to: "/reservations", label: "Reservations" }, { to: "/loaners", label: "Loaners" }, { to: "/rma", label: "RMA" }, { to: "/data-erasure", label: "Data Erasure" }, { to: "/itad", label: "ITAD & Recycle" }, { to: "/discovery", label: "Discovery" }, { to: "/inspection", label: "Inspection" },
    ],
  },
  {
    id: "finance", label: "Finance", icon: Receipt, to: "/invoices",
    children: [
      { to: "/invoices", label: "Invoices" },
      { to: "/finance/order-closure", label: "Order Closure & Reconciliation" },
      { to: "/finance/invoice-copies", label: "Invoice copies" },
      { to: "/finance/whatsapp-followup", label: "Payment Follow-ups" },
      { to: "/finance/whatsapp-followup?bulk=1", label: "Upload Outstanding List" },
      { to: "/finance/whatsapp-followup?view=today", label: "Follow-up · Today" },
      { to: "/finance/whatsapp-followup?view=outstanding", label: "Follow-up · Outstanding" },
      { to: "/finance/whatsapp-followup?view=overdue", label: "Follow-up · Overdue" },
      { to: "/finance/whatsapp-followup?view=verification", label: "Follow-up · Payment Proofs & Verification" },
      { to: "/finance/whatsapp-followup?view=promise", label: "Follow-up · Promise to Pay" },
      { to: "/finance/whatsapp-followup?view=paused", label: "Follow-up · Paused / Disputed" },
      { to: "/finance/whatsapp-followup?view=paid", label: "Follow-up · Paid / Closed" },
      { to: "/finance/whatsapp-followup?view=history", label: "Follow-up · History" },
      { to: "/finance/one-ledger", label: "Ledger" },
    ],
    advanced: [
      { to: "/compliance/kyc/readiness", label: "KYC Readiness Centre" }, { to: "/one-post", label: "ONE POST", financeVault: true }, { to: "/finance/cash-control", label: "Cash Control", financeVault: true }, { to: "/finance/cards", label: "Card Control", financeVault: true },
      { to: "/finance/tally-reconciliation", label: "Tally Reconciliation" }, { to: "/finance/tax-reports", label: "Tax Reports" }, { to: "/finance/compliance-bridge", label: "Tax & Compliance" }, { to: "/commercial/subscriptions", label: "Subscriptions & Billing" },
    ],
  },
  { id: "whatsapp", label: "WhatsApp", icon: MessageCircle, to: "/admin/integrations/whatsapp",
    children: [
      { to: "/admin/integrations/whatsapp?compose=1", label: "+ New Message" },
      { to: "/admin/integrations/whatsapp?tab=messages", label: "Messages & history" },
      { to: "/admin/integrations/whatsapp?tab=templates&view=readiness", label: "Ready templates" },
      { to: "/admin/integrations/whatsapp?tab=scheduled", label: "Scheduled" },
      { to: "/admin/integrations/whatsapp?tab=inbox", label: "Inbox (incoming)" },
    ],
    advanced: [
      { to: "/admin/integrations/whatsapp?tab=campaigns", label: "Campaigns & greetings" }, { to: "/admin/integrations/whatsapp?tab=contacts", label: "Contacts & consent" },
      { to: "/admin/integrations/whatsapp?tab=saved-replies", label: "Saved Replies" }, { to: "/admin/integrations/whatsapp?tab=followups", label: "Automated Follow-ups" },
      { to: "/admin/integrations/whatsapp?tab=settings", label: "WhatsApp Settings (admin)" },
    ] },
  {
    id: "customers", label: "Customers", icon: Users, to: "/commercial/crm/customers",
    children: [
      { to: "/commercial/crm/customers", label: "Customers" },
      { to: "/contacts/relationships", label: "Contacts & Relationships" },
      { to: "/companies", label: "Master Companies" },
      { to: "/trade-partners", label: "Partner List" },
      { to: "/employees", label: "Customer Employees (People)" },
      { to: "/one/relationships", label: "ONE Relationships" },
      { to: "/one/outreach", label: "ONE Outreach" },
      { to: "/people/employee-360", label: "Employee 360" },
    ],
    advanced: [
      { to: "/insights/customer-health", label: "Customer Health" }, { to: "/admin/customer-catalogues", label: "Customer-Specific Catalogues" }, { to: "/compliance/kyc", label: "KYC & Vendor Registration" },
    ],
  },
  {
    id: "analytics", label: "Reports", icon: BarChart3, to: "/insights",
    children: [
      { to: "/insights", label: "Executive" },
      { to: "/insights/commercial", label: "Commercial" },
      { to: "/insights/operations", label: "Operations" },
      { to: "/insights/reports", label: "Reports" },
      { to: "/reports/dc-added-items", label: "Items Added on DCs" },
    ],
    advanced: [
      { to: "/command-centre", label: "Command Centre" }, { to: "/control?tab=sla", label: "SLA Management" }, { to: "/insights/sla", label: "SLA Insights" }, { to: "/exception-centre", label: "Exceptions & Escalations" }, { to: "/audit", label: "Audit Trail" }, { to: "/admin/test-data", label: "Test Data Guard" }, { to: "/admin/subsidiary-reconcile", label: "Subsidiary Reconciliation" },
    ],
  },
];

export const OS_NAV_BOTTOM = [
  { id: "settings", label: "Settings", icon: Settings, to: "/admin/control-centre",
    children: [
      { to: "/admin/control-centre", label: "Control Centre" },
      { to: "/users", label: "Users & Roles", userAdmin: true },
      { to: "/admin/establishments", label: "Establishments" },
      { to: "/admin/integrations", label: "Integrations & API Hub" },
      { to: "/admin/system-health", label: "System Health" },
      { to: "/admin/welcome-labels", label: "Welcome Labels & Tips", onlySuntronix: true },
      { to: "/admin/translations", label: "Translations & Languages", onlySuntronix: true },
      { to: "/settings/welcome-label-branding", label: "Welcome Label Branding" },
      { to: "/settings/experience", label: "My Experience" },
    ],
    advanced: [
      { to: "/admin/one-control-centre", label: "ONE Control Centre" }, { to: "/admin/communications", label: "Communications" }, { to: "/marketing/social-media", label: "Social Media", onlySuntronix: true }, { to: "/admin/communications/email-health", label: "Email Health" }, { to: "/admin/security/vault", label: "Secure Vault" }, { to: "/admin/integration-health", label: "Integration Health" }, { to: "/admin/api-layer", label: "API & Service Layer" },
      { to: "/admin/automation-studio", label: "Automation Studio (ATLAS)" }, { to: "/admin/atlas/flow-definitions", label: "Flow Definitions (ATLAS)" }, { to: "/operations/request-hub", label: "Request Hub Workspace" }, { to: "/admin/failure-centre", label: "Failure Centre (ATLAS)" }, { to: "/admin/automation", label: "Automation Engine" }, { to: "/control?tab=api", label: "API Keys & Webhooks" }, { to: "/knowledge", label: "Policies & Knowledge" },
      { to: "/ai", label: "Suntronix AI (assistant)" }, { to: "/copilot", label: "AI Copilot" }, { to: "/one-ai", label: "ONE AI Workspace" },
      { to: "/aiops", label: "AI Ops (inactive · test data)" }, { to: "/aiops/health", label: "AI Ops · Device Health" }, { to: "/aiops/service-desk", label: "AI Ops · Service Desk" }, { to: "/aiops/alerts", label: "AI Ops · Predictive Alerts" }, { to: "/aiops/remediation", label: "AI Ops · Remediation" },
      { to: "/aiops/compliance", label: "AI Ops · Compliance" }, { to: "/aiops/warranty", label: "AI Ops · Warranty" }, { to: "/aiops/lifecycle", label: "AI Ops · Lifecycle" }, { to: "/aiops/cio", label: "AI Ops · CIO Command Center" }, { to: "/aiops/integrations", label: "AI Ops · Integration Centre" },
    ] },
];

/** Old sidebar group → new workspace (kept for deep links, ⌘K and docs). */
export const NAV_MIGRATION = {
  home: "home", sales: "sales", orders: "orders", operations: "orders (deployment) / inventory (inspection)", inventory: "inventory", logistics: "orders",
  itam: "inventory / customers (people)", service: "service", finance: "finance (Accounts)", analytics: "analytics (Reports)", ai: "settings › advanced", whatsapp: "whatsapp", automations: "settings › advanced", settings: "settings",
};

/** Every existing Space destination is folded into an OS group as "more" so
 *  NOTHING built earlier disappears from navigation. */
const SPACE_TO_GROUP = {
  my_one: "home", documents: "home", sales_desk: "sales", commercial: "sales", customers: "customers",
  technology: "inventory", operations: "orders", insights: "analytics", "legacy-insights": "analytics",
  admin: "settings", one_ai: "settings",
};
const FINANCE_HINT = /^\/(finance|invoices|one-post|commercial\/(buybacks|subscriptions|contracts|amc-pms))/;
const LOGISTICS_HINT = /^\/(shipments|pickups|offboarding|admin\/couriers|insights\/courier|logistics)/;
const ITAM_HINT = /^\/(assets|warranty|reservations|loaners|rma|data-erasure|itad|discovery|data-quality|technology\/asset-age-centre|devices|warehouse)/;
const CUSTOMER_HINT = /^\/(employees|people|companies|contacts|customer-360|commercial\/crm\/customers)/;
const SERVICE_HINT = /^\/(service|service-centre|service-desk|requests|telephony|support|commercial\/csat|operations\/(field-service|applecare|apple-services))/;
const ORDERS_HINT = /^\/(commercial\/(one-orders|crm\/vendor-pos|customer-po)|operations\/employee-fulfilment|orders)/;

function groupFor(spaceId, to) {
  const base = to.split("?")[0];
  if (FINANCE_HINT.test(base)) return "finance";
  if (LOGISTICS_HINT.test(base)) return "orders";
  if (CUSTOMER_HINT.test(base)) return "customers";
  if (ITAM_HINT.test(base)) return "inventory";
  if (SERVICE_HINT.test(base)) return "service";
  if (ORDERS_HINT.test(base)) return "orders";
  return SPACE_TO_GROUP[spaceId] || "settings";
}

(function mergeSpaces() {
  const all = [...OS_NAV, ...OS_NAV_BOTTOM];
  const byId = Object.fromEntries(all.map((g) => [g.id, g]));
  const covered = new Set(all.flatMap((g) => [g.to, ...(g.children || []).map((c) => c.to), ...(g.advanced || []).map((c) => c.to)]).map((t) => t.split("?")[0]));
  const sources = [...SPACES_SUNTRONIX.map((sp) => ({ id: sp.id, label: sp.label, ribbon: sp.ribbon })),
    ...SUBSPACES.map((sp) => ({ id: sp.id, label: sp.label, ribbon: sp.ribbon }))];
  for (const sp of sources) {
    for (const r of sp.ribbon) {
      const base = r.to.split("?")[0];
      if (base === "/" || covered.has(base) || r.to.includes("?")) continue;
      covered.add(base);
      const g = byId[groupFor(sp.id, r.to)];
      if (!g) continue;
      g.more = g.more || [];
      g.more.push({ ...r, space: sp.label });
    }
  }
})();

/** Flat directory of every destination (children + more) for search/⌘K. */
export const OS_DIRECTORY = [...OS_NAV, ...OS_NAV_BOTTOM].flatMap((g) =>
  [...(g.children || []), ...(g.advanced || []), ...(g.more || [])].map((c) => ({ ...c, group: g.label, groupId: g.id })));

/** Which primary group owns a pathname (longest-prefix match). */
export function osGroupForPath(pathname) {
  let best = null, bestLen = -1;
  for (const g of [...OS_NAV, ...OS_NAV_BOTTOM]) {
    const routes = [g.to, ...(g.extra || []), ...(g.children || []).map((c) => c.to), ...(g.advanced || []).map((c) => c.to), ...(g.more || []).map((c) => c.to)];
    for (const r of routes) {
      if (!r || r === "/") continue;
      const base = r.split("?")[0];
      if ((pathname === base || pathname.startsWith(base + "/")) && base.length > bestLen) {
        best = g.id; bestLen = base.length;
      }
    }
  }
  return best || (pathname === "/" || pathname === "/home" ? "home" : null);
}
